'use client'
import {useContext, useRef, useState} from 'react'
import {SanityImageType} from '@local/types'
import {SlideshowNative, SanityImage, Button} from '@local/ui/components'
import {useResizeObserver} from '@local/hooks'
import {cx} from '@local/utils/cx'
import {formatPrice} from '@local/utils/currency'

import CheckIcon from '@local/assets/svgs/check.svg'
import StayContext from '@local/stores/contexts/StayContext'

export type RoomCardProps = {
	title: string;
	description: string;
	images: SanityImageType[];
	tag?: string;
	roomPackages: {
		title: string;
		includes: string[];
		startingPrice: number;
		totalPrice: {
			price: number;
			nightCount: number;
		};
	}[];
};

type RoomPackageProps = {
	title: string;
	includes: string[];
	startingPrice: number;
	isOpen: boolean;
	onToggle: () => void;
	totalPrice: {
		price: number;
		nightCount: number;
	};
};

type ExpandableContentProps = {
	text: string;
	limit?: number;
	textClassName?: string;
	containerClassName?: string;
};

export const RoomCard = ({
	title,
	description,
	_key,
	tag,
	images,
	roomPackages,
}: RoomCardProps) => {
	const [openPackageIndex, setOpenPackageIndex] = useState<number | null>(0)
	const {setRoomPackage, focusGuest, setRoom, setMobileMenuOpen} =
		useContext(StayContext)

	const onSelectPackage = (roomName: string, packageName: string) => {
		setRoomPackage(packageName)
		setRoom(roomName)
		setMobileMenuOpen(true)
		focusGuest.current = true
	}

	const togglePackage = (index: number) => {
		if (openPackageIndex === index) {
			setOpenPackageIndex(null)
		} else {
			setOpenPackageIndex(index)
		}
	}

	return (
		<div className='flex flex-col lg:flex-row gap-20 relative'>
			{tag && (
				<span className='bg-white px-8 py-4 rounded-[3px] text-10 font-medium absolute z-1 top-20 left-20 uppercase text-black'>
					{tag}
				</span>
			)}
			<ImageSlider images={images} />
			<div className='gap-30 flex flex-col'>
				<div>
					<h5 className='text-24 font-[350] text-black'>{title}</h5>
					<ExpandableContent text={description} limit={200} />
				</div>
				{roomPackages?.map((roomPackage, index) => (
					<RoomPackage
						key={roomPackage.title}
						{...roomPackage}
						onSelectPackage={() => onSelectPackage(_key, roomPackage._key)}
						isOpen={openPackageIndex === index}
						onToggle={() => togglePackage(index)} />
				))}
			</div>
		</div>
	)
}

const ImageSlider = ({images}: { images: SanityImageType[] }) => {
	const slideshowRef = useRef(null)
	const scrollContainerWidth = useRef<number>(0)
	const childrenRefs = useRef<Array<HTMLDivElement>>([])

	useResizeObserver(slideshowRef, ({borderBoxSize}) => {
		const slideshowWidth = borderBoxSize[0].inlineSize * images.length
		const childWidth = borderBoxSize[0].inlineSize
		scrollContainerWidth.current = slideshowWidth - childWidth
	})

	const handleScroll = (e) => {
		const relativeScroll = e.target.scrollLeft / scrollContainerWidth.current

		childrenRefs.current.forEach((child, index) => {
			const _innerRatio = relativeScroll * (images.length - 1) - index
			child.style.transform = `translate3d(${_innerRatio * 0.2 * 100}%,0,0)`
		})
	}

	if (!images.length) return <></>

	return (
		<SlideshowNative
			containerRef={slideshowRef}
			containerClassName={cx(
				'relative w-full lg:w-[335px] h-[500px] overflow-hidden  z-0 rounded-10',
			)}
			withDotNavigation
			dotNavigationClassName={cx(
				'absolute bottom-20 left-1/2 -translate-x-1/2 justify-center gap-14',
				'[&>button]:w-34 [&>button]:bg-white',
			)}
			onScroll={handleScroll}
			className={cx('w-full lg:w-[335px] h-full rounded-10')}>
			{images.map((image, index) => (
				<figure
					key={image._key}
					ref={(node) => {
						childrenRefs.current[index] = node as HTMLDivElement
					}}
					className='w-full lg:w-[335px] h-full relative will-change-transform z-0 rounded-lg'>
					<SanityImage
						image={image}
						width={335}
						height={500}
						desktopWidth={335}
						className='h-full w-full object-cover rounded-lg z-0'
						fill />
				</figure>
			))}
		</SlideshowNative>
	)
}

const RoomPackage = ({
	title,
	includes,
	startingPrice,
	totalPrice,
	isOpen,
	onToggle,
	onSelectPackage,
}: RoomPackageProps) => {
	const hasPrice =
		!!totalPrice?.price && !!startingPrice && !!totalPrice?.nightCount

	return (
		<div className='border-t border-grey-1'>
			<div className='flex flex-col justify-between py-16 px-10 lg:items-center lg:flex-row'>
				<div className='flex flex-row justify-between items-center w-full lg:flex-col lg:items-start lg:w-fit gap-y-10'>
					<h5 className='text-18 font-[350] text-black'>{title}</h5>
					<button
						className='text-12 font-medium leading-none underline text-grey-2 uppercase'
						onClick={onToggle}>
						{isOpen ? 'Hide Details' : 'Show Details'}
					</button>
				</div>
				{hasPrice && (
					<div className={cx('flex flex-col', isOpen && 'hidden lg:block')}>
						<p className='text-10 font-medium leading-tight uppercase text-left text-black'>
							starting at:{' '}
							<span className='text-16 font-[350] leading-normal text-dark'>
								{formatPrice(startingPrice)}
							</span>
						</p>
						<p className='text-10 font-medium leading-tight uppercase text-left text-grey-2'>
							total {totalPrice?.nightCount} nights -{' '}
							{formatPrice(totalPrice?.price)}
						</p>
					</div>
				)}
			</div>

			<div
				className={cx(
					'overflow-hidden transition-all duration-300 ease-in-out',
					isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
				)}
				style={{
					transitionProperty: 'max-height, opacity, padding',
				}}>
				<div className='flex flex-col gap-y-12 px-10 pb-16'>
					<span className='text-14 font-[350] text-black'>Includes</span>
					<ul className='flex flex-col gap-y-2'>
						{includes.map((include) => (
							<li key={include} className='flex flex-row items-center gap-x-10'>
								<span className='bg-black rounded-xxs w-14 h-14 flex items-center justify-center'>
									<CheckIcon className='text-12 font-medium leading-tight text-white w-8 h-6' />
								</span>
								<span className='text-14 font-[350] text-grey-2'>
									{include}
								</span>
							</li>
						))}
					</ul>
					{hasPrice && (
						<div className='block lg:hidden px-10 pt-12 pb-24'>
							<p className='text-10 font-medium leading-tight uppercase text-left text-black'>
								starting at:{' '}
								<span className='text-16 font-[350] leading-normal text-dark'>
									{formatPrice(startingPrice)}
								</span>
							</p>
							<p className='text-10 font-medium leading-tight uppercase text-left text-grey-2'>
								total {totalPrice.nightCount} nights -{' '}
								{formatPrice(totalPrice.price)}
							</p>
						</div>
					)}
					<Button
						className='w-full'
						label='Enquire Now'
						variant='primary'
						onClick={onSelectPackage} />
				</div>
			</div>
		</div>
	)
}

const ExpandableContent = ({
	text,
	limit = 120,
	textClassName,
	containerClassName,
}: ExpandableContentProps) => {
	const [expanded, setExpanded] = useState(false)
	const fullTextRef = useRef<HTMLParagraphElement>(null)

	return (
		<div
			className={cx(
				'flex flex-col gap-y-10 overflow-hidden',
				containerClassName,
			)}>

			<div className='overflow-hidden'>
				<p
					ref={fullTextRef}
					className={cx(
						'text-14 text-grey-2 leading-normal font-[350]',
						'transition-all duration-500 ease-in-out',
						textClassName,
					)}
					style={{
						display: '-webkit-box',
						WebkitLineClamp: expanded ? 'unset' : '2',
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
						maxHeight: expanded ? '1000px' : '2.8em',
					}}>
					{text}
				</p>
			</div>

			{text.length > limit && (
				<button
					onClick={() => setExpanded(!expanded)}
					className='text-12 text-black font-medium underline leading-none self-start transition-all duration-300 hover:opacity-80'>
					{expanded ? 'SHOW LESS' : 'READ MORE'}
				</button>
			)}
		</div>
	)
}
